import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { FilingNode } from "v2/types/filingsDashboard.types";

export type FlightRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type FilingFlight = {
  id: string;
  title: string;
  description?: string;
  from: FlightRect;
  to: FlightRect;
};

type FlightRequest = Pick<FilingFlight, "id" | "title" | "description">;

type UseFilingFlightsArgs = {
  filings: FilingNode[];
  scopeKey: string;
  isReady: boolean;
  getOriginRect: () => FlightRect | null;
  getTargetRect: (filingId: string) => FlightRect | null;
  onFlightQueued: (filingId: string) => void;
  onFlightLanded: (filingId: string) => void;
};

type UseFilingFlightsResult = {
  activeFlight: FilingFlight | null;
  landedFilingId: string | null;
  pendingFilingIds: string[];
};

export const FLIGHT_START_DELAY_MS = 640;
export const FLIGHT_DURATION_MS = 1100;
export const FLIGHT_LANDING_MS = 1150;
export const FLIGHT_POP_DURATION_MS = 550;

const EMPTY_QUEUE: FlightRequest[] = [];

const toFlightRequest = (filing: FilingNode): FlightRequest => ({
  id: filing.id,
  title: filing.title,
  description: filing.description,
});

export const useFilingFlights = ({
  filings,
  scopeKey,
  isReady,
  getOriginRect,
  getTargetRect,
  onFlightQueued,
  onFlightLanded,
}: UseFilingFlightsArgs): UseFilingFlightsResult => {
  const reduceMotion = useReducedMotion();

  const filingsRef = useRef(filings);
  filingsRef.current = filings;

  const seenIds = useRef<Set<string>>(new Set());
  const seenScopeKey = useRef<string | null>(null);

  const [queue, setQueue] = useState<FlightRequest[]>(EMPTY_QUEUE);
  const [activeFlight, setActiveFlight] = useState<FilingFlight | null>(null);
  const [landedFilingId, setLandedFilingId] = useState<string | null>(null);

  const filingIdsKey = filings.map((filing) => filing.id).join("|");

  useEffect(() => {
    if (!isReady) return;

    const current = filingsRef.current;
    const currentIds = current.map((filing) => filing.id);

    const isNewScope = seenScopeKey.current !== scopeKey;

    if (isNewScope) {
      seenScopeKey.current = scopeKey;
      seenIds.current = new Set(currentIds);
      setQueue(EMPTY_QUEUE);
      setActiveFlight(null);
      setLandedFilingId(null);
      return;
    }

    const fresh = current.filter((filing) => !seenIds.current.has(filing.id));
    seenIds.current = new Set(currentIds);

    if (fresh.length === 0) return;

    setQueue((pending) => [...pending, ...fresh.map(toFlightRequest)]);
  }, [filingIdsKey, scopeKey, isReady]);

  useEffect(() => {
    if (activeFlight) return undefined;

    const [next, ...rest] = queue;
    if (!next) return undefined;

    onFlightQueued(next.id);

    const timer = setTimeout(() => {
      const from = getOriginRect();
      const to = getTargetRect(next.id);

      setQueue(rest);

      if (reduceMotion || !from || !to) {
        setLandedFilingId(next.id);
        return;
      }

      setActiveFlight({ ...next, from, to });
    }, FLIGHT_START_DELAY_MS);

    return () => clearTimeout(timer);
  }, [
    queue,
    activeFlight,
    getOriginRect,
    getTargetRect,
    onFlightQueued,
    reduceMotion,
  ]);

  useEffect(() => {
    if (!activeFlight) return undefined;

    const timer = setTimeout(() => {
      setLandedFilingId(activeFlight.id);
      setActiveFlight(null);
    }, FLIGHT_LANDING_MS);

    return () => clearTimeout(timer);
  }, [activeFlight]);

  useEffect(() => {
    if (!landedFilingId) return undefined;

    onFlightLanded(landedFilingId);

    const timer = setTimeout(
      () => setLandedFilingId(null),
      FLIGHT_POP_DURATION_MS
    );

    return () => clearTimeout(timer);
  }, [landedFilingId, onFlightLanded]);

  const pendingFilingIds = queue.map((request) => request.id);
  if (activeFlight) pendingFilingIds.push(activeFlight.id);

  return { activeFlight, landedFilingId, pendingFilingIds };
};
